import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';
import { 
  BarChart3, Users, DollarSign, TrendingUp, Activity, 
  Globe, Calendar, RefreshCw, Download, ArrowUp, ArrowDown
} from 'lucide-react';

const InvestorMetrics = () => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const [metricsRes, trendsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/dashboard`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/metrics/trends?days=${period}`, { credentials: 'include' })
      ]);

      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data.metrics);
      }

      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setTrends(data.trends);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load metrics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportMetrics = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/metrics/export`,
        { credentials: 'include' }
      );
      const data = await response.json();
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lifeflo-metrics-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({ title: 'Exported!', description: 'Metrics downloaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Export failed', variant: 'destructive' });
    }
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'purple' }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 bg-${color}-100 dark:bg-${color}-900/20 rounded-full`}>
            <Icon className={`h-6 w-6 text-${color}-500`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center mt-2 text-sm ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend >= 0 ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            <span className="ml-1">{Math.abs(trend)}% vs last period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-gray-500">Unable to load metrics. Admin access required.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Investor Dashboard</h2>
          <p className="text-gray-500">Real-time metrics and analytics</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadMetrics}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={exportMetrics}>
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Users"
          value={metrics.users?.total?.toLocaleString() || 0}
          subtitle={`${metrics.users?.new_this_week || 0} new this week`}
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="DAU"
          value={metrics.users?.dau?.toLocaleString() || 0}
          subtitle="Daily Active Users"
          icon={Activity}
          color="green"
        />
        <MetricCard
          title="MRR"
          value={`$${metrics.revenue?.mrr?.toLocaleString() || 0}`}
          subtitle={`${metrics.revenue?.active_subscriptions || 0} active subs`}
          icon={DollarSign}
          color="purple"
        />
        <MetricCard
          title="Conversion Rate"
          value={`${metrics.revenue?.conversion_rate || 0}%`}
          subtitle="Free to Paid"
          icon={TrendingUp}
          trend={metrics.growth?.wow_growth_percent}
          color="orange"
        />
      </div>

      {/* Engagement & Retention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Engagement Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">Total Tasks Created</span>
              <span className="font-bold">{metrics.engagement?.total_tasks?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">Task Completion Rate</span>
              <span className="font-bold text-green-500">{metrics.engagement?.task_completion_rate}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">AI Messages (Total)</span>
              <span className="font-bold">{metrics.engagement?.total_ai_messages?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">Avg Messages/User</span>
              <span className="font-bold">{metrics.engagement?.avg_messages_per_user}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Retention Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">D1 Retention</span>
              <span className="font-bold text-purple-500">{metrics.retention?.d1_retention}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">Stickiness (DAU/MAU)</span>
              <span className="font-bold text-purple-500">{metrics.retention?.stickiness_percent}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">WoW Growth</span>
              <span className={`font-bold ${metrics.growth?.wow_growth_percent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {metrics.growth?.wow_growth_percent > 0 ? '+' : ''}{metrics.growth?.wow_growth_percent}%
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-gray-600">MoM Growth</span>
              <span className={`font-bold ${metrics.growth?.mom_growth_percent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {metrics.growth?.mom_growth_percent > 0 ? '+' : ''}{metrics.growth?.mom_growth_percent}%
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Geographic Distribution */}
      {metrics.geographic_distribution?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" /> Geographic Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {metrics.geographic_distribution.map((geo, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                  <p className="text-2xl font-bold">{geo.users}</p>
                  <p className="text-sm text-gray-500">{geo.country || 'Unknown'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Feature Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {Object.entries(metrics.feature_usage || {}).map(([feature, count]) => (
              <div key={feature} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                <p className="text-xl font-bold">{count.toLocaleString()}</p>
                <p className="text-xs text-gray-500 capitalize">{feature.replace(/_/g, ' ')}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestorMetrics;
